import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ArrowPathIcon, CheckCircleIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/solid';

import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const toMoney = (value) => {
  const number = Number(value);
  return `$${Number.isFinite(number) ? number.toFixed(2) : '0.00'}`;
};

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim()
    .replace(/\s+/g, ' ');

const normalizeTransactionNumber = (value) =>
  String(value || '').trim().toUpperCase();

const displayHumanName = (value) =>
  String(value || '').toLocaleUpperCase('es-EC');

const getLocalDateInputValue = () => {
  const now = new Date(Date.now());
  const pad = (value) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const createInitialForm = () => ({
  tipo_pago: 'transferencia',
  numero_transaccion: '',
  fecha_pago: getLocalDateInputValue(),
  archivo: null,
  notes: '',
});

const months = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const mergeResponsibleCatalog = (current, additions) => {
  const merged = new Map();
  [...current, ...additions].forEach((responsible) => {
    if (responsible?.id != null) merged.set(String(responsible.id), responsible);
  });
  return [...merged.values()].sort((left, right) =>
    String(left.nombre || '').localeCompare(String(right.nombre || ''), 'es')
  );
};

const JustifyCreditPayments = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [matrizadores, setMatrizadores] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [selectedById, setSelectedById] = useState(() => new Map());
  const [filters, setFilters] = useState({
    company_id: '',
    periodo_year: '',
    periodo_month: '',
    fecha_documento: '',
    usuario_matrizador: '',
    responsible_id: '',
  });
  const [form, setForm] = useState(createInitialForm);
  const [loadingData, setLoadingData] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [duplicateTransactionNumber, setDuplicateTransactionNumber] = useState(null);
  const [successfulSubmitFocusRevision, setSuccessfulSubmitFocusRevision] = useState(null);
  const selectAllRef = useRef(null);
  const reviewPanelRef = useRef(null);
  const firstPanelFieldRef = useRef(null);
  const reviewTriggerRef = useRef(null);
  const primaryFilterActionRef = useRef(null);
  const scopeRevisionRef = useRef(0);
  const rowsRequestRevisionRef = useRef(0);
  const submitRevisionRef = useRef(0);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_contador)) {
      toast.error('No tienes permisos para acceder a esta página.');
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const selectedRows = useMemo(
    () => [...selectedById.values()],
    [selectedById]
  );

  const selectedTotal = useMemo(
    () => selectedRows.reduce((acc, row) => acc + (Number(row.monto) || 0), 0),
    [selectedRows]
  );

  const selectedScope = useMemo(() => ({
    companies: [...new Set(selectedRows.map((row) => row.company?.nombre).filter(Boolean))].sort(),
    dates: [...new Set(selectedRows.map((row) => row.fecha_tramite).filter(Boolean))].sort(),
    matrizadores: [...new Set(selectedRows.map((row) => row.usuario_matrizador).filter(Boolean))].sort(),
    responsibles: [...new Set(selectedRows.map((row) => row.responsible?.nombre).filter(Boolean))].sort(),
  }), [selectedRows]);

  const visibleSelectedCount = useMemo(
    () => rows.reduce((count, row) => count + (selectedById.has(String(row.id)) ? 1 : 0), 0),
    [rows, selectedById]
  );
  const hiddenSelectedCount = Math.max(0, selectedRows.length - visibleSelectedCount);
  const allVisibleSelected = rows.length > 0 && visibleSelectedCount === rows.length;
  const normalizedTransactionNumber = normalizeTransactionNumber(form.numero_transaccion);
  const duplicateTransactionLocked = Boolean(
    duplicateTransactionNumber
    && duplicateTransactionNumber === normalizedTransactionNumber
  );

  const hasRequiredScope = Boolean(filters.company_id && filters.periodo_year && filters.periodo_month);
  const selectedCompany = companies.find((company) => String(company.id) === String(filters.company_id));
  const periodLabel = hasRequiredScope
    ? `${filters.periodo_year}-${String(filters.periodo_month).padStart(2, '0')}`
    : '-';

  const fetchCatalogs = useCallback(async () => {
    const [matrizadoresResponse, companiesResponse] = await Promise.all([
      api.get('/api/audit/matrizadores/list/'),
      api.get('/api/audit/credit-companies/'),
    ]);
    setMatrizadores(Array.isArray(matrizadoresResponse.data) ? matrizadoresResponse.data : []);
    setCompanies(Array.isArray(companiesResponse.data?.results) ? companiesResponse.data.results : []);
  }, []);

  const fetchRows = useCallback(async () => {
    if (!filters.company_id || !filters.periodo_year || !filters.periodo_month) {
      toast.error('Selecciona empresa, año y mes contable antes de cargar créditos.');
      return;
    }
    const requestRevision = ++rowsRequestRevisionRef.current;
    const scopeRevision = scopeRevisionRef.current;
    const isCurrentRequest = () => (
      requestRevision === rowsRequestRevisionRef.current
      && scopeRevision === scopeRevisionRef.current
    );
    setLoadingData(true);
    try {
      const params = {
        company_id: filters.company_id,
        periodo_year: filters.periodo_year,
        periodo_month: filters.periodo_month,
      };
      if (filters.fecha_documento) params.fecha_documento = filters.fecha_documento;
      if (filters.usuario_matrizador) params.usuario_matrizador = filters.usuario_matrizador;
      if (filters.responsible_id) params.responsible_id = filters.responsible_id;
      const response = await api.get('/api/audit/credit-receivables/payment-candidates/', { params });
      if (!isCurrentRequest()) return;
      const loadedRows = Array.isArray(response.data?.results) ? response.data.results : [];
      setRows(loadedRows);
      setSummary(response.data?.summary || null);
      setResponsibles((current) => mergeResponsibleCatalog(
        current,
        loadedRows.map((row) => row.responsible).filter(Boolean)
      ));
    } catch (err) {
      if (!isCurrentRequest()) return;
      toast.error(err?.response?.data?.error || 'No se pudieron cargar los créditos por justificar.');
      setRows([]);
      setSummary(null);
    } finally {
      if (isCurrentRequest()) setLoadingData(false);
    }
  }, [filters]);

  useEffect(() => {
    if (!loading && user?.is_contador) {
      fetchCatalogs().catch(() => toast.error('No se pudieron cargar empresas y matrizadores.'));
    }
  }, [loading, user, fetchCatalogs]);

  useEffect(() => {
    if (!filters.company_id) {
      setResponsibles([]);
      return undefined;
    }

    let cancelled = false;
    const loadResponsibles = async () => {
      try {
        const response = await api.get(
          `/api/audit/credit-responsibles/?company_id=${encodeURIComponent(filters.company_id)}`
        );
        if (!cancelled) {
          const loaded = Array.isArray(response.data?.results) ? response.data.results : [];
          setResponsibles((current) => mergeResponsibleCatalog(loaded, current));
        }
      } catch (err) {
        if (!cancelled) toast.error('No se pudieron cargar los responsables de crédito.');
      }
    };

    loadResponsibles();
    return () => {
      cancelled = true;
    };
  }, [filters.company_id]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = visibleSelectedCount > 0 && !allVisibleSelected;
    }
  }, [visibleSelectedCount, allVisibleSelected]);

  useEffect(() => {
    if (successfulSubmitFocusRevision !== submitRevisionRef.current) return;
    primaryFilterActionRef.current?.focus();
  }, [successfulSubmitFocusRevision]);

  const closePanel = useCallback(() => {
    if (submitInFlightRef.current) return;
    reviewTriggerRef.current?.focus();
    setPanelOpen(false);
  }, []);

  useEffect(() => {
    if (!panelOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    firstPanelFieldRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePanel();
        return;
      }
      if (event.key !== 'Tab' || !reviewPanelRef.current) return;

      const focusable = [...reviewPanelRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [href], [tabindex]:not([tabindex="-1"])'
      )].filter((element) => !element.hasAttribute('hidden'));
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!reviewPanelRef.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [panelOpen, closePanel]);

  const updateScopeFilter = (field, value) => {
    if (submitInFlightRef.current) return;
    scopeRevisionRef.current += 1;
    rowsRequestRevisionRef.current += 1;
    submitRevisionRef.current += 1;
    setDuplicateTransactionNumber(null);
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'company_id' ? { responsible_id: '' } : {}),
    }));
    setRows([]);
    setSummary(null);
    setSelectedById(new Map());
    setForm(createInitialForm());
    setLoadingData(false);
    setSubmitting(false);
    setPanelOpen(false);
    if (field === 'company_id') setResponsibles([]);
  };

  const toggleSelected = (row) => {
    setSelectedById((prev) => {
      const next = new Map(prev);
      const key = String(row.id);
      if (next.has(key)) next.delete(key);
      else next.set(key, row);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedById((prev) => {
      const next = new Map(prev);
      if (allVisibleSelected) {
        rows.forEach((row) => next.delete(String(row.id)));
      } else {
        rows.forEach((row) => next.set(String(row.id), row));
      }
      return next;
    });
  };

  const clearSelection = () => {
    if (submitInFlightRef.current) return;
    submitRevisionRef.current += 1;
    setDuplicateTransactionNumber(null);
    setSelectedById(new Map());
    setPanelOpen(false);
    setForm(createInitialForm());
    setSubmitting(false);
  };

  const submitProof = async (event) => {
    event.preventDefault();
    if (submitInFlightRef.current) return;
    if (selectedRows.length === 0) {
      toast.error('Selecciona al menos un crédito.');
      return;
    }

    if (!form.numero_transaccion || !form.fecha_pago || !form.archivo) {
      toast.error('Completa número de transacción, fecha y archivo.');
      return;
    }

    if (duplicateTransactionLocked) return;
    const submittedTransactionNumber = normalizeTransactionNumber(form.numero_transaccion);

    const formData = new FormData();
    selectedRows.forEach((row) => formData.append('ids', row.id));
    formData.append('tipo_pago', form.tipo_pago);
    formData.append('numero_transaccion', form.numero_transaccion);
    formData.append('fecha_pago', form.fecha_pago);
    formData.append('monto_total', selectedTotal.toFixed(2));
    formData.append('notes', form.notes || '');
    formData.append('archivo', form.archivo);

    const submitRevision = ++submitRevisionRef.current;
    const scopeRevision = scopeRevisionRef.current;
    const isCurrentSubmit = () => (
      submitRevision === submitRevisionRef.current
      && scopeRevision === scopeRevisionRef.current
    );
    submitInFlightRef.current = true;
    setSubmitting(true);
    try {
      await api.post('/api/audit/credit-payments/justify/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!isCurrentSubmit()) return;
      toast.success('Comprobante de crédito enviado para aprobación.');
      setDuplicateTransactionNumber(null);
      setSelectedById(new Map());
      setForm(createInitialForm());
      setPanelOpen(false);
      await fetchRows();
      if (!isCurrentSubmit()) return;
      setSuccessfulSubmitFocusRevision(submitRevision);
    } catch (err) {
      if (!isCurrentSubmit()) return;
      if (err?.response?.data?.code === 'duplicate_receipt_identity') {
        setDuplicateTransactionNumber(submittedTransactionNumber);
      }
      toast.error(err?.response?.data?.error || 'No se pudo justificar el crédito.');
    } finally {
      submitInFlightRef.current = false;
      if (isCurrentSubmit()) setSubmitting(false);
    }
  };

  if (loading) return <div className="text-white text-center">Cargando...</div>;
  if (!user) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-gray-900 p-4 text-white sm:p-6">
      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />

      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Notaría 4 Cantón Otavalo" className="w-12 sm:w-14" />
          <div>
            <h1 className="text-2xl font-bold sm:text-3xl">Justificar trámites a crédito</h1>
            <p className="text-sm text-gray-300">Sube el comprobante exacto de los créditos seleccionados</p>
          </div>
        </div>
        <Link to="/dashboard/contador" className="rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white">
          Volver
        </Link>
      </header>

      <main className="mx-auto max-w-7xl space-y-6">
        <section className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <select
              value={filters.company_id}
              onChange={(e) => updateScopeFilter('company_id', e.target.value)}
              disabled={submitting}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Seleccionar empresa"
            >
              <option value="">Seleccionar empresa</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>{company.nombre}</option>
              ))}
            </select>
            <input
              type="number"
              min="1"
              max="9999"
              value={filters.periodo_year}
              onChange={(e) => updateScopeFilter('periodo_year', e.target.value)}
              disabled={submitting}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              placeholder="Año contable"
              aria-label="Seleccionar año contable"
            />
            <select
              value={filters.periodo_month}
              onChange={(e) => updateScopeFilter('periodo_month', e.target.value)}
              disabled={submitting}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Seleccionar mes contable"
            >
              <option value="">Seleccionar mes contable</option>
              {months.map((month, index) => (
                <option key={month} value={index + 1}>{month}</option>
              ))}
            </select>
            <input
              type="date"
              value={filters.fecha_documento}
              onChange={(e) => setFilters((prev) => ({ ...prev, fecha_documento: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Filtrar por día"
            />
            <select
              value={filters.usuario_matrizador}
              onChange={(e) => setFilters((prev) => ({ ...prev, usuario_matrizador: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Filtrar por matrizador"
            >
              <option value="">Todos los matrizadores</option>
              {matrizadores.map((matrizador) => (
                <option key={matrizador.nombre} value={normalizeText(matrizador.nombre)}>
                  {displayHumanName(matrizador.nombre)}
                </option>
              ))}
            </select>
            <select
              value={filters.responsible_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, responsible_id: e.target.value }))}
              disabled={!filters.company_id}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Filtrar por responsable"
            >
              <option value="">Todos los responsables</option>
              {responsibles.map((responsible) => (
                <option key={responsible.id} value={responsible.id}>
                  {displayHumanName(responsible.nombre)}
                </option>
              ))}
            </select>
            <button
              ref={primaryFilterActionRef}
              type="button"
              onClick={fetchRows}
              disabled={!hasRequiredScope || loadingData}
              className="rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cargar créditos
            </button>
            <button
              type="button"
              onClick={fetchRows}
              disabled={!hasRequiredScope || loadingData}
              className="rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Recargar créditos"
            >
              <ArrowPathIcon className={`inline h-5 w-5 ${loadingData ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-lg bg-notaria-blue-1/80 p-4">
            <p className="text-sm text-gray-300">Créditos visibles</p>
            <p className="text-2xl font-bold">{summary?.count || 0}</p>
            <p className="mt-1 text-xs text-gray-300">{selectedCompany?.nombre || 'Sin empresa'} / {periodLabel}</p>
          </div>
          <div className="rounded-lg bg-notaria-blue-1/80 p-4">
            <p className="text-sm text-gray-300">Total visible</p>
            <p className="text-2xl font-bold">{toMoney(summary?.total)}</p>
          </div>
          <div className="rounded-lg bg-notaria-blue-1/80 p-4">
            <p className="text-sm text-gray-300">Seleccionado</p>
            <p className="text-2xl font-bold">{selectedRows.length} / {toMoney(selectedTotal)}</p>
          </div>
        </section>

        <section className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl">
          {selectedRows.length > 0 && (
            <div className="mb-3 flex justify-end">
              <div
                data-testid="selection-action-dock"
                className="inline-flex max-w-full flex-wrap items-center justify-end gap-2 rounded-lg border border-notaria-yellow/40 bg-notaria-blue-1 px-2 py-2 shadow-2xl sm:px-3"
              >
                <div className="flex min-w-0 flex-wrap items-baseline justify-end gap-x-2 gap-y-0.5 px-1" aria-live="polite">
                  <span className="text-sm font-bold">
                    {selectedRows.length} {selectedRows.length === 1 ? 'seleccionado' : 'seleccionados'}
                  </span>
                  <span className="tabular-nums font-bold text-notaria-yellow">{toMoney(selectedTotal)}</span>
                  {hiddenSelectedCount > 0 && (
                    <span className="text-xs text-gray-300">
                      {hiddenSelectedCount} {hiddenSelectedCount === 1 ? 'selección oculta' : 'selecciones ocultas'}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={submitting}
                  title="Limpiar selección"
                  aria-label="Limpiar selección"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-200 transition-colors hover:bg-notaria-blue-2 hover:text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow focus:ring-offset-2 focus:ring-offset-notaria-blue-1"
                >
                  <TrashIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                <button
                  ref={reviewTriggerRef}
                  type="button"
                  onClick={() => setPanelOpen(true)}
                  className="max-w-full rounded-md bg-notaria-yellow px-3 py-2 text-sm font-bold text-notaria-blue-1 transition-colors hover:bg-notaria-red hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-notaria-blue-1 sm:px-4"
                >
                  Revisar y justificar
                </button>
              </div>
            </div>
          )}
          <div data-testid="credit-table-scroll" className="max-h-[52vh] overflow-auto sm:max-h-[60vh]">
            <table className="w-full min-w-[980px] border-collapse text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-notaria-blue-2 text-left">
                  <th className="p-3">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      disabled={rows.length === 0}
                      className="h-5 w-5 rounded text-notaria-yellow disabled:opacity-40"
                      aria-label="Seleccionar todos los créditos visibles"
                    />
                  </th>
                  <th className="p-3">Día</th>
                  <th className="p-3">Matrizador</th>
                  <th className="p-3">Empresa</th>
                  <th className="p-3">Responsable</th>
                  <th className="p-3">Trámite</th>
                  <th className="p-3 whitespace-nowrap">No. Comprobante</th>
                  <th className="p-3">Monto</th>
                </tr>
              </thead>
              <tbody>
                {loadingData ? (
                  <tr><td colSpan="8" className="p-4 text-center text-gray-300">Cargando...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan="8" className="p-4 text-center text-gray-400">No hay créditos por justificar con los filtros actuales.</td></tr>
                ) : rows.map((row) => (
                  <tr key={row.id} className="border-b border-notaria-blue-2 hover:bg-notaria-blue-2/40">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedById.has(String(row.id))}
                        onChange={() => toggleSelected(row)}
                        className="h-5 w-5 rounded text-notaria-yellow"
                        aria-label={`Seleccionar crédito ${row.tramite?.no_temporal || row.id}`}
                      />
                    </td>
                    <td className="p-3">{row.fecha_tramite}</td>
                    <td className="p-3">{displayHumanName(row.usuario_matrizador) || '-'}</td>
                    <td className="p-3">{row.company?.nombre || '-'}</td>
                    <td className="p-3">{displayHumanName(row.responsible?.nombre) || '-'}</td>
                    <td className="p-3">
                      <p className="font-semibold">{row.tramite?.no_temporal || '-'}</p>
                      <p className="text-xs text-gray-300">{row.tramite?.acto || '-'}</p>
                    </td>
                    <td className="p-3 whitespace-nowrap">{row.tramite?.no_comprobante || '-'}</td>
                    <td className="p-3 font-semibold">{toMoney(row.monto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {selectedRows.length > 0 && (
        <div
          data-testid="credit-review-overlay"
          hidden={!panelOpen}
          inert={!panelOpen}
          aria-hidden={panelOpen ? undefined : true}
          className={`fixed inset-0 z-50 items-center justify-center bg-black/60 md:p-4 ${panelOpen ? 'flex' : 'hidden'}`}
        >
          <form
            ref={reviewPanelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="credit-review-title"
            onSubmit={submitProof}
            className="flex h-[100dvh] w-full flex-col overflow-hidden bg-notaria-blue-1 shadow-2xl md:h-auto md:max-h-[calc(100dvh-2rem)] md:max-w-4xl md:rounded-lg"
          >
            <header
              data-testid="credit-review-header"
              className="flex shrink-0 items-center justify-between gap-4 border-b border-notaria-blue-2 px-4 py-3 sm:px-6"
            >
              <div className="min-w-0">
                <h2 id="credit-review-title" className="text-xl font-bold">Revisar justificación</h2>
                <div className="mt-0.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
                  <span className="text-gray-300">
                    {selectedRows.length} {selectedRows.length === 1 ? 'crédito seleccionado' : 'créditos seleccionados'}
                  </span>
                  <span className="tabular-nums font-bold text-notaria-yellow">{toMoney(selectedTotal)}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={closePanel}
                disabled={submitting}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-gray-200 transition-colors hover:bg-notaria-blue-2 hover:text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow focus:ring-offset-2 focus:ring-offset-notaria-blue-1"
                aria-label="Cerrar panel de revisión"
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </header>

            <div
              data-testid="credit-review-scroll"
              className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6"
            >
              <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] md:gap-0">
                <section aria-label="Resumen de la selección" className="space-y-3 border-b border-notaria-blue-2 pb-5 text-sm md:border-b-0 md:border-r md:pb-0 md:pr-6">
                  <h3 className="text-base font-bold">Selección</h3>
                  <p>Empresa: <span className="font-semibold">{selectedScope.companies.join(', ') || '-'}</span></p>
                  <p>Período contable: <span className="tabular-nums font-semibold">{periodLabel}</span></p>
                  <details className="border-t border-notaria-blue-2 pt-3">
                    <summary className="cursor-pointer text-sm font-semibold text-notaria-yellow focus:outline-none focus:ring-2 focus:ring-notaria-yellow focus:ring-offset-2 focus:ring-offset-notaria-blue-1">
                      Ver detalle de la selección
                    </summary>
                    <div className="mt-3 space-y-2 text-gray-200">
                      <p>Días: <span className="tabular-nums font-semibold">{selectedScope.dates.join(', ') || '-'}</span></p>
                      <p>Matrizadores: <span className="font-semibold">{selectedScope.matrizadores.map(displayHumanName).join(', ') || '-'}</span></p>
                      <p>Responsables: <span className="font-semibold">{selectedScope.responsibles.map(displayHumanName).join(', ') || '-'}</span></p>
                    </div>
                  </details>
                </section>

                <section aria-label="Datos del comprobante" className="space-y-4 md:pl-6">
                  <h3 className="text-base font-bold">Comprobante</h3>
                  <label className="block text-sm font-semibold">
                    Tipo de pago
                    <select
                      ref={firstPanelFieldRef}
                      value={form.tipo_pago}
                      onChange={(e) => setForm((prev) => ({ ...prev, tipo_pago: e.target.value }))}
                      className="mt-1 w-full rounded-md bg-notaria-blue-2 p-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    >
                      <option value="transferencia">Transferencia</option>
                      <option value="deposito">Depósito</option>
                      <option value="tarjeta_credito">Tarjeta de Crédito</option>
                      <option value="nota_credito">Nota de Crédito</option>
                    </select>
                  </label>
                  <label className="block text-sm font-semibold">
                    Número de transacción
                    <input
                      required
                      value={form.numero_transaccion}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        numero_transaccion: e.target.value,
                      }))}
                      aria-describedby={duplicateTransactionLocked ? 'duplicate-transaction-status' : undefined}
                      className="mt-1 w-full rounded-md bg-notaria-blue-2 p-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    />
                  </label>
                  {duplicateTransactionLocked && (
                    <p
                      id="duplicate-transaction-status"
                      role="alert"
                      className="rounded-md border border-notaria-yellow/60 bg-notaria-blue-2 p-3 text-sm font-semibold text-notaria-yellow"
                    >
                      Cambia el número de transacción para volver a enviar.
                    </p>
                  )}
                  <label className="block text-sm font-semibold">
                    Fecha de pago
                    <input
                      required
                      type="date"
                      value={form.fecha_pago}
                      onChange={(e) => setForm((prev) => ({ ...prev, fecha_pago: e.target.value }))}
                      className="mt-1 w-full rounded-md bg-notaria-blue-2 p-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    />
                  </label>
                  <label className="block text-sm font-semibold">
                    Archivo del comprobante
                    <input
                      required
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => {
                        const archivo = e.target.files?.[0] || null;
                        setForm((prev) => ({ ...prev, archivo }));
                      }}
                      className="mt-1 w-full rounded-md bg-notaria-blue-2 p-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    />
                  </label>
                  <label className="block text-sm font-semibold">
                    Notas
                    <textarea
                      value={form.notes}
                      onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                      className="mt-1 min-h-[96px] w-full rounded-md bg-notaria-blue-2 p-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    />
                  </label>
                </section>
              </div>
            </div>

            <footer
              data-testid="credit-review-footer"
              className="flex shrink-0 flex-col gap-2 border-t border-notaria-blue-2 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:px-6"
            >
              <button
                type="button"
                onClick={closePanel}
                disabled={submitting}
                className="rounded-md px-4 py-2 font-semibold text-gray-200 transition-colors hover:bg-notaria-blue-2 hover:text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow focus:ring-offset-2 focus:ring-offset-notaria-blue-1"
              >
                Cancelar
              </button>
              <button
                disabled={submitting || duplicateTransactionLocked}
                className={`rounded-md bg-notaria-yellow px-4 py-2.5 text-sm font-bold text-notaria-blue-1 transition-colors hover:bg-notaria-red hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-notaria-blue-1 disabled:cursor-not-allowed disabled:opacity-50 ${submitting ? 'opacity-50' : ''}`}
              >
                {submitting ? <ArrowPathIcon className="mr-2 inline h-5 w-5 animate-spin" aria-hidden="true" /> : <CheckCircleIcon className="mr-2 inline h-5 w-5" aria-hidden="true" />}
                Enviar comprobante de crédito · {toMoney(selectedTotal)}
              </button>
            </footer>
          </form>
        </div>
      )}
    </motion.div>
  );
};

export default JustifyCreditPayments;
